import React, { useState, useEffect, useMemo } from "react";
import "../../styles/createAppointmentStyles.css";
import { Button } from "react-md";
import { useRefundDialog } from "./useRefundDialog";
import { RefundDialog } from "./RefundDialog";
import PaymentLinkDialog from "../payFromBridge/PaymentLinkDialog";
import {
  getPaymentDisplayState,
  getPaymentStatusDisplay,
  getRefundStatusFromEventHistory,
  getRefundTotalFromEventHistory,
} from "../../helper/paymentViewModel";
import { formatCurrency } from "../../helper/formatCurrency";
import RefreshButton from "../../helper/RefreshButton";
import GlobalSkeletonLoader from "../../../../../../components/common/GlobalSkeletonLoader";
import { EventHistoryTable, buildEventRows } from "./EventHistoryTable";

const cardStyle = {
  width: "100%",
  margin: "36px 0",
  border: "2px solid #D9D9D9",
  borderRadius: "16px",
  padding: "32px",
  boxSizing: "border-box",
};
const headingStyle = { fontWeight: 700, fontSize: "24px", color: "#000000" };
const orderIdLabelStyle = {
  fontWeight: 400,
  fontSize: "11px",
  color: "#999999",
  marginBottom: "4px",
};
const orderIdValueStyle = {
  fontWeight: 600,
  fontSize: "16px",
  color: "#000000",
};
const labelStyle = {
  fontWeight: 400,
  fontSize: "12px",
  color: "#757575",
  marginBottom: "4px",
};
const valueStyle = { fontWeight: 600, fontSize: "16px", color: "#26284D" };
// max-content columns size to the widest row, which is what keeps "Total refund
// amount" under "Total amount" and "Refund status" under "Payment status".
const amountsGridStyle = {
  display: "grid",
  gridTemplateColumns: "max-content max-content auto",
  columnGap: "47px",
  rowGap: "25px",
  alignItems: "center",
  marginTop: "24px",
};

const EventHistoryPaymentDetails = ({
  appointment,
  fetchAppointment,
  resetDashboardReload,
  profileDetails,
  onRefresh,
  loaderToCheckPaymentStatus,
}) => {
  const { dialogState, setDialogState, openRefundDialog, handleClose } =
    useRefundDialog();
  const [showPaymentLinkDialog, setShowPaymentLinkDialog] = useState(false);

  // Reuse the existing view model for the header/action area (order id, total
  // amount, generate/share link, refund button) so behaviour stays consistent
  // with PaymentAndRefund — only the table is driven by eventHistory.
  const view = useMemo(
    () => getPaymentDisplayState(appointment),
    [appointment],
  );

  // Pass the appointment's own payment/appointment state so a payment leg left stuck at PENDING
  // by a dead link is reported against reality rather than as a perpetual "In progress".
  const rows = useMemo(
    () =>
      buildEventRows(appointment?.eventHistory, {
        paymentStatusResponse: appointment?.paymentStatusResponse,
        appointmentStatus: appointment?.status,
      }),
    [appointment],
  );

  const paymentStatusDisplay = getPaymentStatusDisplay(appointment);
  const refundStatusDisplay = getRefundStatusFromEventHistory(
    appointment?.eventHistory,
  );
  const refundTotal = getRefundTotalFromEventHistory(appointment?.eventHistory);
  // Cart appointments are paid across several orders, so a single "Zyla order ID"
  // would name just one of them — the table's Order ID column carries them all.
  const showOrderId = !appointment?.cartId;
  const orderId = appointment?.paymentOrderIds?.[0] ?? "";

  useEffect(() => {
    if (view.isPending) {
      setShowPaymentLinkDialog(true);
    }
  }, []);

  const refreshAptDetailScreenData = () => {
    fetchAppointment();
    resetDashboardReload();
  };

  if (loaderToCheckPaymentStatus) {
    return (
      <div style={cardStyle}>
        <div style={headingStyle}>Payment details</div>
        <br />
        <GlobalSkeletonLoader />
      </div>
    );
  }

  const paymentAction = view.showGenerateLink ? (
    <Button
      raised
      className="zy-btn-orange"
      onClick={() => setShowPaymentLinkDialog(true)}
    >
      Generate payment link
    </Button>
  ) : view.showPaymentLinkShared ? (
    <Button
      raised
      className="zy-btn-green-success"
      onClick={() => setShowPaymentLinkDialog(true)}
    >
      Payment link shared
    </Button>
  ) : view.showFailureLabel ? (
    <Button raised className="zy-btn-red" disabled>
      {view.failureLabel}
    </Button>
  ) : null;

  return (
    <div style={cardStyle}>
      {/* ── Heading + reload ── */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <div style={headingStyle}>Payment details</div>
        <div style={{ marginLeft: "32px", display: "flex" }}>
          <RefreshButton onRefresh={onRefresh} loading={false} />
        </div>

        {view.showRefundButton && (
          <div style={{ marginLeft: "auto" }}>
            <Button
              raised
              secondary
              onClick={() =>
                openRefundDialog({
                  appointmentObj: appointment,
                  isOnlyRefund: true,
                  onRefresh: refreshAptDetailScreenData,
                })
              }
            >
              Refund
            </Button>
            <RefundDialog
              dialogState={dialogState}
              setDialogState={setDialogState}
              handleClose={handleClose}
            />
          </div>
        )}
      </div>

      {/* ── Zyla order ID (non-cart appointments only) ── */}
      {showOrderId && (
        <div style={{ marginTop: "24px" }}>
          <div style={orderIdLabelStyle}>Zyla order ID</div>
          <div style={orderIdValueStyle}>{orderId || "-"}</div>
        </div>
      )}

      {/* ── Amounts · statuses · payment link action ── */}
      <div style={amountsGridStyle}>
        <div>
          <div style={labelStyle}>Total amount</div>
          <div style={valueStyle}>{`Rs ${view.price}`}</div>
        </div>
        <div>
          {paymentStatusDisplay.show && (
            <>
              <div style={labelStyle}>Payment status</div>
              <div style={{ ...valueStyle, color: paymentStatusDisplay.color }}>
                {paymentStatusDisplay.label}
              </div>
            </>
          )}
        </div>
        {/* 47px column gap + 103px = the 150px the design asks for */}
        <div style={{ marginLeft: "103px" }}>{paymentAction}</div>

        {refundStatusDisplay.show && (
          <>
            <div>
              <div style={labelStyle}>Total refund amount</div>
              <div style={valueStyle}>{formatCurrency(refundTotal) || "-"}</div>
            </div>
            <div>
              <div style={labelStyle}>Refund status</div>
              <div style={{ ...valueStyle, color: refundStatusDisplay.color }}>
                {refundStatusDisplay.label}
              </div>
            </div>
            <div />
          </>
        )}
      </div>

      {/* ── Unified payment / refund table (driven by eventHistory) ── */}
      <div style={{ marginTop: "24px" }}>
        <EventHistoryTable rows={rows} />
      </div>

      {/* ── Payment link dialog ── */}
      {showPaymentLinkDialog && (
        <PaymentLinkDialog
          visible={showPaymentLinkDialog}
          onClose={() => setShowPaymentLinkDialog(false)}
          appointment={appointment}
          refreshAptDetailScreenData={
            view.isPending ? refreshAptDetailScreenData : undefined
          }
          profileDetails={profileDetails}
        />
      )}
    </div>
  );
};

export default EventHistoryPaymentDetails;
